import React, { useState, useEffect } from "react";
import { Image as ImageIcon } from "lucide-react";

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * DNH ShareCard — 1080×1080 social share card.
 * Uses /dnh-logo-h.png brand asset.
 *
 * Props: theme, title, description, category, confidence,
 *        date, imageUrl, storyUrl, scale
 */
export const ShareCard = ({
  theme = "dark",
  title = "Bomb Threats Disrupt Approximately 10 European Flights",
  description = "Bomb threats were made against about 10 European flights, including several by Ryanair, on Tuesday. Airport closures and security checks were implemented following the incidents.",
  category = "CRIME",
  confidence = "High confidence",
  date = "3 Nov 2026",
  imageUrl = null,
  storyUrl = "www.dailynewshighlights.com",
  scale = 1,
  style = {},
}) => {
  const dark = theme === "dark";

  const c = {
    bg:            dark ? "#0f0f0f" : "#ffffff",
    imgBg:         dark ? "#1c1c1c" : "#e8e5e1",
    headlineTxt:   dark ? "#ffffff" : "#0f0f0f",
    descTxt:       dark ? "#cccccc" : "#444444",   // brighter — more readable
    dateTxt:       dark ? "#999999" : "#888888",
    subTxt:        dark ? "#888888" : "#999999",
    domainTxt:     dark ? "#ffffff" : "#0f0f0f",
    separator:     dark ? "#282828" : "#e0ddd9",
    confidenceTxt: dark ? "#ffffff" : "#1a1a1a",
    orange:        "#E84B1A",
    green:         "#22c55e",
    ctaBg:         dark ? "#E84B1A" : "#0f0f0f",
    ctaTxt:        "#ffffff",
    logoPillBg:    dark ? "#ffffff" : "transparent",
    logoPillRadius: dark ? "10px" : "0",
    logoPillPad:   dark ? "8px 14px" : "0",
    imgPlaceholder:    dark ? "#555" : "#bbb",
    imgPlaceholderTxt: dark ? "#555" : "#bbb",
  };

  // ── Layout constants (all in px at 1080 base) ──────────────
  const W = 1080;
  const H = 1080;
  const P = 48;         // outer horizontal padding
  const IMG_H = 340;    // image block height — slightly reduced to give content more room

  return (
    <div
      style={{
        width:  `${W * scale}px`,
        height: `${H * scale}px`,
        overflow: "hidden",
        position: "relative",
        flexShrink: 0,
        ...style,
      }}
    >
      <div
        data-testid={`share-card-${theme}`}
        style={{
          width:  `${W}px`,
          height: `${H}px`,
          backgroundColor: c.bg,
          transform: scale !== 1 ? `scale(${scale})` : "none",
          transformOrigin: "top left",
          display: "flex",
          flexDirection: "column",
          boxSizing: "border-box",
          fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica Neue, Arial, sans-serif",
          overflow: "hidden",
        }}
      >
        {/* ── TOP BAR ─────────────────────────────────────── */}
        <div
          data-testid="share-card-top-bar"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: `36px ${P}px 28px`,
            flexShrink: 0,
          }}
        >
          <div
            data-testid="share-card-logo"
            style={{
              backgroundColor: c.logoPillBg,
              borderRadius: c.logoPillRadius,
              padding: c.logoPillPad,
              display: "inline-flex",
              alignItems: "center",
            }}
          >
            <img
              src="/dnh-logo-h.png"
              alt="Daily News Highlights"
              style={{ height: "46px", width: "auto", display: "block" }}
            />
          </div>

          <div
            data-testid="share-card-date"
            style={{
              color: c.dateTxt,
              fontSize: "22px",
              fontFamily: "ui-monospace, 'SF Mono', Menlo, Consolas, monospace",
              fontWeight: 500,
            }}
          >
            {date}
          </div>
        </div>

        {/* ── STORY IMAGE ────────────────────────────────── */}
        <div
          data-testid="share-card-image-block"
          style={{
            margin: `0 ${P}px`,
            height: `${IMG_H}px`,
            borderRadius: "12px",
            backgroundColor: c.imgBg,
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={title}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "12px" }}>
              <ImageIcon size={44} color={c.imgPlaceholder} strokeWidth={1.5} />
              <span style={{ color: c.imgPlaceholderTxt, fontSize: "16px", fontFamily: "ui-monospace, Consolas, monospace" }}>
                [Story image · 984 × {IMG_H}]
              </span>
            </div>
          )}
        </div>

        {/* ── CONTENT ────────────────────────────────────── */}
        <div
          data-testid="share-card-content"
          style={{
            flex: 1,
            padding: `24px ${P}px 32px`,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            boxSizing: "border-box",
            minHeight: 0,
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            {/* Category + Confidence */}
            <div style={{ display: "flex", alignItems: "center", gap: "18px" }}>
              <div
                data-testid="share-card-category"
                style={{
                  backgroundColor: c.orange,
                  color: "#fff",
                  fontSize: "15px",
                  fontWeight: 800,
                  letterSpacing: "0.08em",
                  padding: "6px 18px",
                  borderRadius: "6px",
                  display: "inline-block",
                }}
              >
                {category}
              </div>
              <div
                data-testid="share-card-confidence"
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <div style={{ width: "10px", height: "10px", borderRadius: "50%", backgroundColor: c.green, flexShrink: 0 }} />
                <span style={{ color: c.confidenceTxt, fontSize: "17px", fontWeight: 600 }}>
                  {confidence}
                </span>
              </div>
            </div>

            {/* Headline */}
            <h1
              data-testid="share-card-headline"
              style={{
                fontSize: "52px",
                fontWeight: 900,
                color: c.headlineTxt,
                lineHeight: 1.1,
                letterSpacing: "-0.025em",
                margin: 0,
              }}
            >
              {title}
            </h1>

            {/* Description — now much more prominent */}
            <p
              data-testid="share-card-description"
              style={{
                fontSize: "21px",
                color: c.descTxt,
                lineHeight: 1.55,
                margin: 0,
                fontWeight: 400,
              }}
            >
              {description}
            </p>
          </div>

          {/* Separator */}
          <div style={{ height: "1px", backgroundColor: c.separator, margin: "0" }} />

          {/* Bottom row */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "20px" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
              <span
                data-testid="share-card-domain"
                style={{ color: c.domainTxt, fontSize: "17px", fontWeight: 700 }}
              >
                {storyUrl.replace(/^https?:\/\//, "")}
              </span>
              <span style={{ color: c.subTxt, fontSize: "13px", fontWeight: 400 }}>
                Understand any country in 10 minutes
              </span>
            </div>

            <div
              data-testid="share-card-cta"
              style={{
                backgroundColor: c.ctaBg,
                color: c.ctaTxt,
                fontSize: "19px",
                fontWeight: 700,
                padding: "16px 36px",
                borderRadius: "9999px",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              Read the full brief →
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


/* ── Preview page — real live stories ──────────────────────── */
const CAT = {
  "politics": "POLITICS", "world": "WORLD", "business-economy": "BUSINESS",
  "technology": "TECHNOLOGY", "science": "SCIENCE", "health": "HEALTH",
  "sports": "SPORTS", "entertainment": "ENTERTAINMENT", "climate-environment": "CLIMATE",
  "crime": "CRIME",
};

function fmt(iso) {
  if (!iso) return new Date().toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso.slice(0, 10); }
}

export const ShareCardPreview = () => {
  const [stories, setStories] = useState([]);
  const [sel, setSel] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${BASE_URL}/api/today`, { credentials: "include" })
      .then(r => r.json())
      .then(d => {
        const all = [d.lead_story, ...(d.featured_stories || [])].filter(Boolean);
        const withImg = all.filter(s => s.image_url);
        setStories((withImg.length ? withImg : all).slice(0, 8));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const s = stories[sel];
  const props = s ? {
    title:       s.headline || s.title || "Untitled",
    description: s.summary  || s.description || "",
    category:    CAT[s.category_slug] || (s.category_slug || "WORLD").toUpperCase(),
    confidence:  s.confidence ? `${s.confidence} confidence` : "High confidence",
    date:        fmt(s.published_at || s.event_latest_at),
    imageUrl:    s.image_url || null,
    storyUrl:    "www.dailynewshighlights.com",
  } : {
    title: "Fetching today's story…",
    description: "Pulling the latest stories from the live feed.",
    category: "WORLD", confidence: "High confidence",
    date: fmt(null), imageUrl: null, storyUrl: "www.dailynewshighlights.com",
  };

  return (
    <div
      data-testid="share-card-preview-page"
      style={{
        minHeight: "100vh",
        backgroundColor: "#111",
        padding: "40px 20px 60px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}
    >
      <h1 style={{ color: "#fff", fontSize: "20px", fontWeight: 700, margin: "0 0 4px", textAlign: "center" }}>
        Social Share Card — 1080 × 1080
      </h1>
      <p style={{ color: "#888", fontSize: "13px", margin: "0 0 20px", textAlign: "center" }}>
        Live story · 1:1 actual size
      </p>

      {/* Story selector */}
      {stories.length > 1 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", maxWidth: "1200px", justifyContent: "center", marginBottom: "28px" }}>
          {stories.map((st, i) => (
            <button key={i} onClick={() => setSel(i)}
              style={{
                background: i === sel ? "#E84B1A" : "#222", color: i === sel ? "#fff" : "#aaa",
                border: "none", borderRadius: "6px", padding: "5px 12px",
                fontSize: "12px", fontWeight: 600, cursor: "pointer",
                maxWidth: "220px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}
            >
              {(st.headline || st.title || `Story ${i + 1}`).slice(0, 38)}
            </button>
          ))}
        </div>
      )}

      {loading && <p style={{ color: "#888", fontSize: "14px" }}>Fetching live stories…</p>}

      {/* Cards — actual 1080px each, side by side */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "32px", justifyContent: "center", alignItems: "flex-start" }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
          <span style={{ background: "#E84B1A", color: "#fff", fontSize: "11px", fontWeight: 700, letterSpacing: "0.12em", padding: "3px 14px", borderRadius: "999px" }}>DARK</span>
          <div style={{ borderRadius: "14px", overflow: "hidden", boxShadow: "0 8px 48px rgba(0,0,0,0.7)" }}>
            <ShareCard {...props} theme="dark" scale={1} />
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
          <span style={{ background: "#e0ddd9", color: "#111", fontSize: "11px", fontWeight: 700, letterSpacing: "0.12em", padding: "3px 14px", borderRadius: "999px" }}>LIGHT</span>
          <div style={{ borderRadius: "14px", overflow: "hidden", boxShadow: "0 8px 48px rgba(0,0,0,0.4)", border: "1px solid #ccc" }}>
            <ShareCard {...props} theme="light" scale={1} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareCardPreview;
